import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Reveal, Eyebrow, SectionHeading } from "@/components/Reveal";

const CONTROLS = [
  { key: "staff", label: "Number of team members", min: 2, max: 40, step: 1, unit: "", testid: "calc-slider-staff" },
  { key: "notes", label: "Average notes per staff member per day", min: 1, max: 12, step: 1, unit: "", testid: "calc-slider-notes" },
  { key: "days", label: "Working days per week", min: 3, max: 7, step: 1, unit: "", testid: "calc-slider-days" },
  { key: "current", label: "Average minutes currently spent per note", min: 10, max: 60, step: 1, unit: " min", testid: "calc-slider-current" },
  { key: "verachart", label: "Estimated VeraChart minutes per note", min: 2, max: 15, step: 1, unit: " min", testid: "calc-slider-verachart" },
  { key: "teams", label: "Number of teams", min: 1, max: 20, step: 1, unit: "", testid: "calc-slider-teams" },
];

const fmt1 = (n) => n.toLocaleString("en-US", { maximumFractionDigits: 1 });
const fmt0 = (n) => Math.round(n).toLocaleString("en-US");

export function Calculator() {
  const [values, setValues] = useState({ staff: 8, notes: 4, days: 5, current: 24, verachart: 5, teams: 1 });

  const r = useMemo(() => {
    const notesWeek = values.staff * values.notes * values.days;
    const currentHrs = (notesWeek * values.current) / 60;
    const vcHrs = (notesWeek * values.verachart) / 60;
    const weekly = Math.max(currentHrs - vcHrs, 0);
    return {
      notesWeek,
      currentHrs,
      vcHrs,
      weekly,
      monthly: weekly * 4.33,
      yearly: weekly * 52,
    };
  }, [values]);

  return (
    <section id="impact" className="bg-white py-20 lg:py-28" aria-labelledby="impact-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <Eyebrow>Scale the Impact</Eyebrow>
          <SectionHeading id="impact-heading">A Few Minutes Per Note Becomes Hundreds of Staff Hours</SectionHeading>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            A community-based team of 8 staff completing 4–5 client notes per person per day could spend 50+ staff
            hours each week on documentation. VeraChart is designed to dramatically reduce that administrative
            burden. Model your own program below — the defaults reflect the workflow from our recorded demonstration.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14" data-testid="time-math-table">
            <h3 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
              One note is a coffee break. A team week is a caseload.
            </h3>
            <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="px-6 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">If this team…</th>
                    <th className="px-6 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Without prompt</th>
                    <th className="px-6 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">With VeraChart</th>
                    <th className="px-6 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-teal-700">Time back</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    ["1 note", "24 min", "≈5 min", "≈19 min"],
                    ["1 staff × 4 notes / day", "1 hr 36 min", "≈20 min", "≈1 hr 16 min"],
                    ["8 staff × 4 notes / day", "12 hr 48 min", "≈2 hr 40 min", "≈10 hr 8 min"],
                    ["That team, 5-day week", "64 hours", "≈13.3 hours", "≈51 hours"],
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0">
                      <td className="px-6 py-4 font-medium text-navy">{row[0]}</td>
                      <td className="px-6 py-4 font-mono text-slate-600">{row[1]}</td>
                      <td className="px-6 py-4 font-mono text-slate-600">{row[2]}</td>
                      <td className="px-6 py-4 font-mono font-bold text-teal-700">{row[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-6 max-w-2xl font-display text-lg font-semibold leading-snug text-navy">
              51 hours a week is not a software metric.{" "}
              <span className="text-slate-500">It is field time, family contact, and fewer notes written at 9 p.m.</span>
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-5" data-testid="time-calculator">
          <Reveal className="lg:col-span-2">
            <div className="h-full rounded-2xl border border-slate-200 bg-slate-50 p-7">
              {CONTROLS.map((c) => (
                <div key={c.key} className="mb-7 last:mb-0">
                  <div className="mb-3 flex items-baseline justify-between gap-3">
                    <label htmlFor={c.testid} className="text-sm font-medium text-navy">{c.label}</label>
                    <span className="font-mono text-lg font-bold text-teal-700">{values[c.key]}{c.unit}</span>
                  </div>
                  <Slider
                    id={c.testid}
                    data-testid={c.testid}
                    min={c.min}
                    max={c.max}
                    step={c.step}
                    value={[values[c.key]]}
                    onValueChange={([v]) => setValues((prev) => ({ ...prev, [c.key]: v }))}
                    aria-label={c.label}
                  />
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-3">
            <div className="flex h-full flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <p className="font-mono text-3xl font-bold text-navy" data-testid="calc-notes-week">{fmt0(r.notesWeek)}</p>
                  <p className="mt-1.5 text-xs text-slate-500">Total notes per week</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <p className="font-mono text-3xl font-bold text-navy" data-testid="calc-current-hours">{fmt1(r.currentHrs)}</p>
                  <p className="mt-1.5 text-xs text-slate-500">Current documentation hours / week</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <p className="font-mono text-3xl font-bold text-teal-700" data-testid="calc-vc-hours">{fmt1(r.vcHrs)}</p>
                  <p className="mt-1.5 text-xs text-slate-500">Est. VeraChart documentation hours / week</p>
                </div>
              </div>

              <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-2xl bg-teal-600 p-6 text-white">
                  <p className="font-mono text-3xl font-bold" data-testid="calc-recovered-week">{fmt1(r.weekly)}</p>
                  <p className="mt-1.5 text-xs text-teal-100">Potential staff hours recovered / week</p>
                </div>
                <div className="rounded-2xl bg-navy p-6 text-white">
                  <p className="font-mono text-3xl font-bold" data-testid="calc-recovered-month">{fmt0(r.monthly)}</p>
                  <p className="mt-1.5 text-xs text-slate-300">Potential staff hours recovered / month</p>
                </div>
                <div className="rounded-2xl bg-navy p-6 text-white">
                  <p className="font-mono text-3xl font-bold" data-testid="calc-recovered-year">{fmt0(r.yearly)}</p>
                  <p className="mt-1.5 text-xs text-slate-300">Potential staff hours recovered / year</p>
                </div>
              </div>

              <p className="rounded-xl border border-teal-200 bg-teal-50 p-5 text-sm text-navy" data-testid="calc-orgwide-banner">
                <span className="font-mono text-lg font-bold text-teal-700">{fmt1(r.weekly * values.teams)}</span>{" "}
                potential organization-wide staff hours recovered / week across{" "}
                <strong>{values.teams}</strong> team{values.teams === 1 ? "" : "s"} —{" "}
                <span className="font-mono font-bold text-teal-700">{fmt0(r.yearly * values.teams)}</span> / year
              </p>

              <p className="text-xs leading-relaxed text-slate-400">
                Illustrative calculator — not a guarantee of savings. Actual results will vary by organization, staff
                member, documentation requirements, and workflow. Hours are intentionally not converted into dollars;
                apply your own labor-cost assumptions.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
